/**
 * ShpfltSeenOnInfluencers — Influencer showcase with video controls and carousel navigation.
 *
 * Features:
 * - Multiple video source support (YouTube, Vimeo, Shopify-hosted)
 * - Autoplay with IntersectionObserver (muted by default)
 * - Mute/unmute toggle
 * - Flickity carousel navigation
 * - Active item scaling
 *
 * @example
 * <shpflt-seen-on-influencers data-section-id="{{ section.id }}" data-autoplay="true" data-carousel="false">
 *   ...
 * </shpflt-seen-on-influencers>
 */

import { ShopigurusHTMLElement } from '@shopigurus/base';

class ShpfltSeenOnInfluencers extends ShopigurusHTMLElement {
  /** @type {IntersectionObserver|null} */
  #videoObserver = null;

  /** @type {Map<number, boolean>} */
  #videoMuteState = new Map();

  /** @type {Flickity|null} */
  #flickity = null;

  connectedCallback() {
    super.connectedCallback();

    // Initialize component on first connect
    this.#initializeComponent();

    // Re-initialize when section is reloaded in theme editor (e.g., when settings change)
    document.addEventListener('shopify:section:load', this.#handleSectionLoad, {
      signal: this.abortController.signal
    });
  }

  /**
   * Initialize component (called on connect and section reload).
   */
  #initializeComponent() {
    // Wait for window load event to ensure vendor.js (with Flickity) has fully loaded
    // The load event fires AFTER all deferred scripts, stylesheets, and resources
    // Unlike DOMContentLoaded, which can fire before deferred scripts finish executing
    if (document.readyState === 'complete') {
      // Page already loaded, init immediately
      this.#init();
    } else {
      // Wait for load event
      window.addEventListener('load', () => this.#init(), { once: true });
    }
  }

  /**
   * Handle section reload in theme editor.
   * @param {CustomEvent} e
   * @listens {shopify:section:load}
   */
  #handleSectionLoad = (e) => {
    // Only reinitialize if this is our section
    if (e.detail.sectionId === this.dataset.sectionId) {
      // Clean up existing instances before reinitializing
      this.#cleanup();
      // Reinitialize with updated DOM
      this.#initializeComponent();
    }
  }

  /**
   * Initialize all component features after DOM and vendor.js are ready.
   */
  #init() {
    this.#initCarousel();
    this.#initVideoAutoplay();
    this.#initMuteButtons();
    this.#initPlayButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Clean up observers, carousel, and state.
   */
  #cleanup() {
    this.#destroyCarousel();
    this.#videoObserver?.disconnect();
    this.#videoMuteState.clear();
  }

  /**
   * Pause all animations when section is selected in editor.
   */
  onSectionSelect() {
    this.#pauseAllVideos();
  }

  /**
   * Resume animations when section is deselected.
   */
  onSectionDeselect() {
    if (this.dataset.autoplay === 'true') {
      this.#resumeVisibleVideos();
    }
  }

  /**
   * Scroll to and highlight block when selected in editor.
   * @param {CustomEvent} e
   */
  onBlockSelect(e) {
    const blockId = e.detail?.blockId;
    if (!blockId) return;

    const block = this.querySelector(`[data-shopify-editor-block="${blockId}"]`);
    if (!block) return;

    // Scroll to block
    block.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    block.classList.add('editor-selected');
  }

  /**
   * Remove highlight from block when deselected.
   * @param {CustomEvent} e
   */
  onBlockDeselect(e) {
    const blockId = e.detail?.blockId;
    if (!blockId) return;

    const block = this.querySelector(`[data-shopify-editor-block="${blockId}"]`);
    if (block) {
      block.classList.remove('editor-selected');
    }
  }

  /**
   * Update active item class for scaling based on playing state.
   */
  #updateActiveItem() {
    if (this.dataset.activeScale !== 'true') return;

    const items = this.querySelectorAll('.shpflt-seen-on-influencers__item');
    items.forEach((item) => {
      const isPlaying = item.getAttribute('data-playing') === 'true';
      item.classList.toggle('shpflt-seen-on-influencers__item--active', isPlaying);
    });
  }


  /**
   * Initialize flickity carousel when enabled.
   * Called after window load event to ensure vendor.js has fully loaded Flickity.
   */
  #initCarousel() {
    if (this.dataset.carousel !== 'true') return;

    const grid = this.querySelector('.shpflt-seen-on-influencers__grid');
    if (!grid || !grid.classList.contains('flickity-slider--default')) return;

    // Flickity loaded by vendor.js (deferred) - guaranteed available after window load event
    if (typeof themeVendor?.Flickity === 'undefined') {
      console.error('Flickity not available - check that vendor.js loaded correctly');
      return;
    }

    // Calculate pages based on block count and columns
    const blockCount = parseInt(this.dataset.blockCount, 10) || 0;
    const columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 3;
    const columnsMobile = parseInt(this.dataset.columnsMobile, 10) || 2;

    // Check if viewport is mobile (< 768px)
    const isMobile = window.innerWidth < 768;
    const columnsPerView = isMobile ? columnsMobile : columnsDesktop;
    const pageCount = Math.ceil(blockCount / columnsPerView);

    // Only show navigation if there's more than 1 page
    const hasMultiplePages = pageCount > 1;
    const navigationTypeAttr = this.dataset.navigationType || 'dots';
    const showDots = navigationTypeAttr === 'dots' && hasMultiplePages;
    const showArrows = navigationTypeAttr === 'arrows' && hasMultiplePages;

    try {
      this.#flickity = new themeVendor.Flickity(grid, {
        cellAlign: 'left',
        contain: true,
        pageDots: showDots,
        prevNextButtons: showArrows,
        groupCells: true,
        adaptiveHeight: false,
        wrapAround: false,
        arrowShape: 'M4.068 18.649l15.466 16.105c.31.283.466.629.466 1.039 0 .41-.155.77-.466 1.08a1.412 1.412 0 0 1-1.038.424c-.41 0-.756-.141-1.038-.424L.466 19.708A1.373 1.373 0 0 1 0 18.648c0-.423.155-.776.466-1.059L17.458.424A1.412 1.412 0 0 1 18.496 0c.41 0 .755.141 1.038.424.31.282.466.636.466 1.06 0 .423-.155.776-.466 1.059L4.068 18.649z',
      });
    } catch (error) {
      console.error('Flickity initialization failed:', error);
    }
  }

  /**
   * Destroy flickity instance on disconnect.
   */
  #destroyCarousel() {
    if (this.#flickity && typeof this.#flickity.destroy === 'function') {
      this.#flickity.destroy();
      this.#flickity = null;
    }
  }

  /**
   * Initialize video autoplay with IntersectionObserver.
   */
  #initVideoAutoplay() {
    if (this.dataset.autoplay !== 'true') return;

    const mediaWrappers = this.querySelectorAll('[ref^="mediaWrapper-"]');
    if (mediaWrappers.length === 0) return;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const wrapper = entry.target;
          const video = wrapper.querySelector('video');

          if (entry.isIntersecting) {
            if (video) {
              video.play().catch(() => {
                // Autoplay failed - browser policy
              });
            }
          } else {
            if (video) {
              video.pause();
            }
          }
        }
      },
      { threshold: 0.5, rootMargin: '0px' }
    );

    for (const wrapper of mediaWrappers) {
      this.#videoObserver.observe(wrapper);
    }
  }

  /**
   * Initialize mute/unmute buttons for videos.
   */
  #initMuteButtons() {
    const muteButtons = this.querySelectorAll('[ref^="muteBtn-"]');

    for (const btn of muteButtons) {
      const refName = btn.getAttribute('ref');
      const index = parseInt(refName.replace('muteBtn-', ''), 10);

      // Initialize mute state
      this.#videoMuteState.set(index, true);

      btn.addEventListener('click', () => this.#toggleMute(index, btn), { signal: this.abortController.signal });
    }
  }

  /**
   * Toggle mute state for a video.
   * @param {number} index
   * @param {HTMLButtonElement} btn
   */
  #toggleMute(index, btn) {
    const currentMuted = this.#videoMuteState.get(index) ?? true;
    const newMuted = !currentMuted;

    this.#videoMuteState.set(index, newMuted);

    // Update button aria-label to reflect current state
    const muteLabel = newMuted ? 'Unmute video' : 'Mute video';
    btn.setAttribute('aria-label', muteLabel);
    btn.setAttribute('data-muted', newMuted ? 'true' : 'false');

    // Find the video element
    const wrapper = this.querySelector(`[ref="mediaWrapper-${index}"]`);
    if (!wrapper) return;

    const video = wrapper.querySelector('video');
    const iframe = wrapper.querySelector('iframe');

    if (video) {
      video.muted = newMuted;
    } else if (iframe) {
      // For YouTube/Vimeo, we need to use postMessage
      const videoType = wrapper.querySelector('[data-video-type]')?.dataset?.videoType;

      if (videoType === 'youtube') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: newMuted ? 'mute' : 'unMute' }),
          '*'
        );
      } else if (videoType === 'vimeo') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ method: 'setVolume', value: newMuted ? 0 : 1 }),
          '*'
        );
      }
    }
  }

  /**
   * Initialize play/pause buttons for all video types.
   */
  #initPlayButtons() {
    const playButtons = this.querySelectorAll('[ref^="playBtn-"]');
    const pauseButtons = this.querySelectorAll('[ref^="pauseBtn-"]');

    for (const btn of playButtons) {
      const refName = btn.getAttribute('ref');
      const index = parseInt(refName.replace('playBtn-', ''), 10);

      btn.addEventListener('click', () => this.#playVideo(index, btn), { signal: this.abortController.signal });
    }

    for (const btn of pauseButtons) {
      const refName = btn.getAttribute('ref');
      const index = parseInt(refName.replace('pauseBtn-', ''), 10);

      btn.addEventListener('click', () => this.#pauseVideo(index, btn), { signal: this.abortController.signal });
    }
  }

  /**
   * Play a video and update UI state.
   * @param {number} index
   * @param {HTMLButtonElement} playBtn
   */
  #playVideo(index, playBtn) {
    const wrapper = this.querySelector(`[ref="mediaWrapper-${index}"]`);
    if (!wrapper) return;

    const video = wrapper.querySelector('video');
    const iframe = wrapper.querySelector('iframe');
    const videoType = wrapper.querySelector('[data-video-type]')?.dataset?.videoType;
    const item = wrapper.closest('.shpflt-seen-on-influencers__item');
    const pauseBtn = this.querySelector(`[ref="pauseBtn-${index}"]`);

    if (video) {
      // Shopify-hosted video
      video.play().then(() => {
        playBtn.setAttribute('data-playing', 'true');
        playBtn.setAttribute('aria-label', 'Play video');
        if (pauseBtn) {
          pauseBtn.setAttribute('data-playing', 'true');
          pauseBtn.setAttribute('aria-label', 'Pause video');
        }
        if (item) {
          item.setAttribute('data-playing', 'true');
        }
        this.#updateActiveItem();
      }).catch(() => {
        // Play failed
      });
    } else if (iframe && videoType === 'youtube') {
      // YouTube video
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'playVideo' }),
        '*'
      );
      playBtn.setAttribute('data-playing', 'true');
      playBtn.setAttribute('aria-label', 'Play video');
      if (pauseBtn) {
        pauseBtn.setAttribute('data-playing', 'true');
        pauseBtn.setAttribute('aria-label', 'Pause video');
      }
      if (item) {
        item.setAttribute('data-playing', 'true');
      }
      this.#updateActiveItem();
    } else if (iframe && videoType === 'vimeo') {
      // Vimeo video
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'play' }),
        '*'
      );
      playBtn.setAttribute('data-playing', 'true');
      playBtn.setAttribute('aria-label', 'Play video');
      if (pauseBtn) {
        pauseBtn.setAttribute('data-playing', 'true');
        pauseBtn.setAttribute('aria-label', 'Pause video');
      }
      if (item) {
        item.setAttribute('data-playing', 'true');
      }
      this.#updateActiveItem();
    }
  }

  /**
   * Pause a video and update UI state.
   * @param {number} index
   * @param {HTMLButtonElement} pauseBtn
   */
  #pauseVideo(index, pauseBtn) {
    const wrapper = this.querySelector(`[ref="mediaWrapper-${index}"]`);
    if (!wrapper) return;

    const video = wrapper.querySelector('video');
    const iframe = wrapper.querySelector('iframe');
    const videoType = wrapper.querySelector('[data-video-type]')?.dataset?.videoType;
    const item = wrapper.closest('.shpflt-seen-on-influencers__item');
    const playBtn = this.querySelector(`[ref="playBtn-${index}"]`);

    if (video) {
      // Shopify-hosted video
      video.pause();
      pauseBtn.setAttribute('data-playing', 'false');
      pauseBtn.setAttribute('aria-label', 'Pause video');
      if (playBtn) {
        playBtn.setAttribute('data-playing', 'false');
        playBtn.setAttribute('aria-label', 'Play video');
      }
      if (item) {
        item.setAttribute('data-playing', 'false');
      }
      this.#updateActiveItem();
    } else if (iframe && videoType === 'youtube') {
      // YouTube video
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo' }),
        '*'
      );
      pauseBtn.setAttribute('data-playing', 'false');
      pauseBtn.setAttribute('aria-label', 'Pause video');
      if (playBtn) {
        playBtn.setAttribute('data-playing', 'false');
        playBtn.setAttribute('aria-label', 'Play video');
      }
      if (item) {
        item.setAttribute('data-playing', 'false');
      }
      this.#updateActiveItem();
    } else if (iframe && videoType === 'vimeo') {
      // Vimeo video
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'pause' }),
        '*'
      );
      pauseBtn.setAttribute('data-playing', 'false');
      pauseBtn.setAttribute('aria-label', 'Pause video');
      if (playBtn) {
        playBtn.setAttribute('data-playing', 'false');
        playBtn.setAttribute('aria-label', 'Play video');
      }
      if (item) {
        item.setAttribute('data-playing', 'false');
      }
      this.#updateActiveItem();
    }
  }

  /**
   * Pause all videos in the section.
   */
  #pauseAllVideos() {
    const videos = this.querySelectorAll('video');
    for (const video of videos) {
      video.pause();
    }
  }

  /**
   * Resume visible videos (used when section is deselected in editor).
   */
  #resumeVisibleVideos() {
    // Re-observe to trigger autoplay for visible videos
    if (this.#videoObserver) {
      const mediaWrappers = this.querySelectorAll('[ref^="mediaWrapper-"]');
      for (const wrapper of mediaWrappers) {
        this.#videoObserver.unobserve(wrapper);
        this.#videoObserver.observe(wrapper);
      }
    }
  }
}

customElements.define('shpflt-seen-on-influencers', ShpfltSeenOnInfluencers);
